import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useAppDispatch, RootState } from '@/store';
import { getScheduleList } from '@/store/schedule/summary/action';
import { getUserVenues } from '@/store/user-info/action';
import { ReactComponent as AddIcon } from '@/assets/icons/ri-add-line.svg';
import useCustomPaging from '@/hooks/useCustomPaging';
import useDatePickerFilter from '@/hooks/useDatePickerFilter';
import useGenericSelectFilter from '@/hooks/useGenericSelectFilter';
import useSearchFilter from '@/hooks/useSearchFilter';
import useUserVenueSelectFilter from '@/hooks/useUserVenueSelectFilter';
import { dynamicAttr } from '@/utils/fallback';
import { constructTableMetaFromPaging2 } from '@/utils/paging';
import Button from '@/components/common/Button';
import BreadcrumbLayout from "@/components/common/CustomLayout/BreadcrumbLayout";
import Table from '@/components/common/Table';
import GenericDatePicker from '@/components/common/GenericDatePicker';
import {
	renderStatusRow,
	HEADERS,
	SORT,
	renderDetailRow,
	renderTableEditButton,
	renderReasonRow,
} from '@/pages/ClosedSchedule/List/Table';
import { STATUS_SELECT_OPTIONS } from '@/pages/ClosedSchedule/List/constant';
import UserVenueSelect from "@/components/common/UserVenueSelect";
import GenericSelect from "@/components/common/GenericSelect";

const statusSelectOptions = STATUS_SELECT_OPTIONS.map(s => ({
	value: s,
	label: s
}))

const renderScheduleHeader = (
	searchString,
	handleChangeSearchString,
	handleSubmitSearch,
	navigate: Function,
	venueOptions,
	selectedVenue,
	handleChangeVenue
) => (
	<div className="flex justify-between items-center gap-8 p-0">
		<div className="flex flex-row items-center gap-4">
			<p className="font-bold text-2xl text-black-primary">Closed Schedule</p>
			<div className="bg-grey-line-subtle h-7 w-1"/>
			<UserVenueSelect
				options={venueOptions}
				value={selectedVenue}
				placeholder='Select venue'
				onChange={handleChangeVenue}
			/>
		</div>
		<div className="flex items-center gap-4">
			<input
				value={searchString}
				onChange={handleChangeSearchString}
				onKeyDown={handleSubmitSearch}
				name="searchString"
				type="text"
				className="flex items-center py-[8px] px-4 gap-2 border rounded border-solid border-stroke-subtle text-jsm font-normal"
				placeholder="Search for schedules"
			/>
			<div className="bg-grey-line-subtle h-6 w-1"/>
			<Button type="submit" variant="primary" className="text-white whitespace-nowrap" onClick={() => navigate('/dashboard/calendar/closed-schedule/add')}>
				<AddIcon/>
				<p>Closed Schedule</p>
			</Button>
		</div>
	</div>
)
const renderFilterSection = (
	{ filterMethods }: any,
	selectedDate: Date,
	selectedStatus: string,
	selectStatusOptions: string[],
	handleChangeStatus: any,
	handleChangeSelectedDate: any
) => (
	<div className="flex w-full px-4 pt-6">
		<div className="flex justify-between items-center w-full py-4 px-6 bg-white border border-grey-line-subtle border-b-0 rounded-t">
			<div className="flex items-center p-0 gap-6">
				<div className="flex items-start">
					<FormProvider {...filterMethods}>
						<Controller
							name='filterStatus'
							render={({ field }) =>
								<GenericSelect
									styles={{
										control: style => ({ ...style, width: '12em' }),
									}}
									options={selectStatusOptions}
									value={selectedStatus}
									placeholder='Select status'
									{...filterMethods.register('filterStatus')}
									{...field}
									onChange={handleChangeStatus}
								/>
							}
						/>
					</FormProvider>
				</div>
				<div className="bg-grey-line-subtle h-6 w-1"/>
				<GenericDatePicker
					selected={selectedDate}
					onChange={handleChangeSelectedDate}
				/>
			</div>
			<div className="flex items-center p-0 gap-2">
				{/* TODO: To dropdown and do actual pagination */}
				10 Item Per Page
			</div>
		</div>
	</div>
)

const ClosedSchedule = () => {
	const dispatch = useAppDispatch()
	const schedules = useSelector((state: RootState) => state.scheduleList.state.items)
	const paging = useSelector((state: RootState) => state.scheduleList.state.paging)
	const isLoading = useSelector((state: RootState) => state.scheduleList.loading)
	const [searchParams,] = useSearchParams()
	const [currentPageFilter,,handlePageNumberChange] = useCustomPaging()
	const [
		selectedDateTimestamp,
		selectedDate,
		handleChangeSelectedDate
	] = useDatePickerFilter('schedule')
	const [
		searchString,
		currentSearchFilter,
		handleChangeSearchString,
		handleSubmitSearch
	] = useSearchFilter()
	const [
		currentVenueIdFilter,
		selectedVenue,
		venueOptions,
		handleChangeVenue,
	] = useUserVenueSelectFilter()
	const [
		currentStatusFilter,
		selectedStatus,
		selectStatusOptions,
		handleChangeStatus,
	] = useGenericSelectFilter('status', statusSelectOptions)
	const filterMethods = useForm()
	const navigate = useNavigate()

	const setSortingParam = (key, order) => order === 'ASC' ? key : `-${key}`

	const getAllSchedules = () => {
		const params = {
			sizeMeta: 10,
			...dynamicAttr('searchMeta', currentSearchFilter),
			...dynamicAttr('venueMeta', currentVenueIdFilter),
			...dynamicAttr('scheduleMeta', selectedDateTimestamp),
			...dynamicAttr('statusMeta', currentStatusFilter),
			...dynamicAttr('pageMeta', currentPageFilter),
			...dynamicAttr('sortMeta', setSortingParam(searchParams.get('sortBy') ?? "createdAt", searchParams.get('order'))),
		}
		dispatch(getScheduleList(params))
	}

	const scheduleMeta: ScheduleFilter = {
		status: searchParams.get('status'),
		schedule: searchParams.get('schedule'),
		search: searchParams.get('search'),
		venue: searchParams.get('venue')
	}

	useEffect(() => {
		(async () => {
			dispatch(getUserVenues({}))
		})()
	}, [])
	useEffect(() => {
		(async () => {
			await getAllSchedules()
		})()
	}, [
		scheduleMeta.search,
		currentStatusFilter,
		currentPageFilter,
		currentVenueIdFilter,
		currentSearchFilter,
		selectedDateTimestamp,
		searchParams.get('sortBy'),
		searchParams.get('order')
	])

	const constructTableData = (data) => {
		const tableBody = data.map(schedule => {
			const {
				id,
				reason,
				status,
				details
			} = schedule
			return {
				id,
				value: {
					reason,
					schedule: [...details],
					status,
					action: id
				}
			}
		})
		return {
			data: tableBody,
			component: {
				reason: (v: string) => renderReasonRow(v),
				schedule: (v: ScheduleTableDetailParams[]) => renderDetailRow(v),
				status: (v: string) => renderStatusRow(v),
				action: (v: string) => renderTableEditButton(v, navigate)
			}
		}
	}

	return (
		<BreadcrumbLayout>
			<div className='flex flex-col w-full gap-8'>
			 {renderScheduleHeader(
			 	 searchString,
				 handleChangeSearchString,
				 handleSubmitSearch,
				 navigate,
				 venueOptions,
				 selectedVenue,
				 handleChangeVenue
			 )}
				<div className="flex flex-col">
					{renderFilterSection(
						{ filterMethods },
						selectedDate,
						selectedStatus,
						selectStatusOptions,
						handleChangeStatus,
						handleChangeSelectedDate
					)}
					{!isLoading && <div className='flex p-4 pt-0 w-full'>
						<Table headers={HEADERS}
									 body={constructTableData(schedules)}
									 sort={SORT}
									 meta={constructTableMetaFromPaging2(paging)}
									 pageNumberHandler={handlePageNumberChange}/>
					</div>}
				</div>
			</div>
		</BreadcrumbLayout>)
}

export default ClosedSchedule;
